import React, { useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { OrganizationContext } from '@BUP/contexts/OrganizationContext'
import { requiresExactCourseAssignmentPreferences } from '@BUP/services/BraveUp'
import UserIcon from '@BUP/assets/Icons/user_icon.svg'
import PageLayout from '@BUP/components/Layout/Page'
import TitleDashboard from '@BUP/components/Generics/TitleDashboard'
import BupSpinner from '@BUP/components/Loading/Spinner'
import AlertComponent from '@BUP/components/Generics/Alert'
import PreferencesForm from '@BUP/components/CourseAssignment/PreferencesForm'
import { useGetCurrentPreferencesPeriod } from '@BUP/hooks/CourseAssignment/useGetCurrentPreferencesPeriod'
import { useGetMyPreferences } from '@BUP/hooks/CourseAssignment/useGetMyPreferences'
import { useSavePreferences } from '@BUP/hooks/CourseAssignment/useSavePreferences'

// Fallback only for the brief window before `period` loads - the real limit always comes from the
// backend (Organization.courseAssignmentMaxPreferences, defaulting to the system default there),
// since it's a per-school setting, not something the frontend should hardcode or guess.
const DEFAULT_MAX_SELECTION = 5

const CourseAssignmentPreferencesPage = () => {
  const [t] = useTranslation('translation', { keyPrefix: 'pages.data.courseAssignmentPreferences' })
  const organization = useContext(OrganizationContext)

  const {
    isLoading: isLoadingPeriod,
    period,
    classmates,
    error: errorPeriod
  } = useGetCurrentPreferencesPeriod()

  const {
    isLoading: isLoadingPreferences,
    classmateEnrollmentIds,
    error: errorPreferences
  } = useGetMyPreferences({ enabled: !!period })

  const {
    mutateAsync: savePreferences,
    isPending: isSubmitting,
    error: errorSaving
  } = useSavePreferences()

  const handleSubmit = useCallback((selectedClassmateIds) => {
    savePreferences(selectedClassmateIds)
  }, [savePreferences])

  const renderContent = () => {
    if (isLoadingPeriod || (period && isLoadingPreferences)) {
      return <BupSpinner size='large' />
    }

    if (errorPeriod || errorPreferences) {
      return (
        <AlertComponent
          type='error'
          messageKey='common.resources.course-assignment.actions.read.failure'
          error={errorPeriod || errorPreferences}
        />
      )
    }

    if (!period || period.status !== 'open') {
      return <AlertComponent type='info' message={t('noPeriodOpen')} />
    }

    const maxSelection = period.maxPreferences ?? DEFAULT_MAX_SELECTION
    // Capped at the number of eligible classmates, so a small course (fewer classmates than the
    // max) doesn't leave the student unable to ever save.
    const requiredSelection = requiresExactCourseAssignmentPreferences(organization)
      ? Math.min(maxSelection, classmates.length)
      : null

    return (
      <>
        <p>
          {requiredSelection
            ? t('descriptionExact', { required: requiredSelection })
            : t('description', { max: maxSelection })}
        </p>
        <PreferencesForm
          classmatesOptions={classmates}
          initialSelection={classmateEnrollmentIds}
          maxSelection={maxSelection}
          requiredSelection={requiredSelection}
          isPeriodOpen={period.status === 'open'}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
        {errorSaving && (
          <AlertComponent
            type='error'
            messageKey='common.resources.course-assignment.actions.update.failure'
            error={errorSaving}
          />
        )}
      </>
    )
  }

  return (
    <PageLayout>
      <>
        <TitleDashboard icon={UserIcon} text={t('title')} />
        {renderContent()}
      </>
    </PageLayout>
  )
}

export default CourseAssignmentPreferencesPage
